import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Empty, List, Modal, Space, Typography, theme as antTheme } from 'antd';
import { BellOutlined, CheckOutlined, MailOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ar';
import http from '../../store/services/http-common';
import useTranslation from '../../localization/useTranslation';
import { isAuthenticated } from '../../utilities/authentication';
import {
  NOTIFICATIONS_BELL_LIMIT,
  notificationsListQueryOptions,
  unreadCountQueryOptions,
} from '../../query/fetchers/notifications';
import { queryKeys } from '../../query/queryKeys';

dayjs.extend(relativeTime);

const NotificationsBell = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, language } = useTranslation();
  const { theme } = useSelector((state) => state.theme);
  const { token } = antTheme.useToken();
  const [open, setOpen] = useState(false);

  const iconColor = theme === 'dark' ? '#ffffff' : token.colorText;
  const authenticated = isAuthenticated();

  const unreadQuery = useQuery({
    ...unreadCountQueryOptions(),
    enabled: authenticated,
  });

  const listQuery = useQuery({
    ...notificationsListQueryOptions(NOTIFICATIONS_BELL_LIMIT, 1, open),
    enabled: authenticated && open,
  });

  const unreadCount = unreadQuery.data ?? 0;
  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const hasMore = total > NOTIFICATIONS_BELL_LIMIT;
  const loading = listQuery.isFetching;

  useEffect(() => {
    if (typeof listQuery.data?.unreadCount === 'number') {
      queryClient.setQueryData(
        queryKeys.notifications.unreadCount(),
        listQuery.data.unreadCount
      );
    }
  }, [listQuery.data?.unreadCount, queryClient]);

  useEffect(() => {
    dayjs.locale(language === 'ar' ? 'ar' : 'en');
  }, [language]);

  const setUnreadCount = useCallback((next) => {
    queryClient.setQueryData(queryKeys.notifications.unreadCount(), next);
  }, [queryClient]);

  const patchBellList = useCallback((updater) => {
    queryClient.setQueryData(
      queryKeys.notifications.list(NOTIFICATIONS_BELL_LIMIT, 1),
      updater
    );
  }, [queryClient]);

  const openModal = () => {
    setOpen(true);
  };

  const goToNotificationsPage = () => {
    setOpen(false);
    navigate('/notifications');
  };

  const markAsRead = async (notification) => {
    if (notification.is_read) {
      return;
    }
    try {
      await http.post(`/notifications/${notification.id}/read`);
      patchBellList((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) =>
            item.id === notification.id
              ? { ...item, is_read: true, read_at: new Date().toISOString() }
              : item
          ),
        };
      });
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch {
      // Ignore; list will refresh on next open.
    }
  };

  const markAllAsRead = async () => {
    try {
      await http.post('/notifications/read-all');
      patchBellList((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) => ({
            ...item,
            is_read: true,
            read_at: item.read_at || new Date().toISOString(),
          })),
          unreadCount: 0,
        };
      });
      setUnreadCount(0);
      queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] });
    } catch {
      // Ignore.
    }
  };

  const handleItemClick = async (notification) => {
    await markAsRead(notification);
    const link = notification?.data?.link;
    if (typeof link === 'string' && link.startsWith('/')) {
      setOpen(false);
      navigate(link);
    }
  };

  if (!authenticated) {
    return null;
  }

  return (
    <>
      <Badge count={unreadCount} size="small" offset={[-2, 4]}>
        <Button
          type="text"
          aria-label={t('notifications.title')}
          onClick={openModal}
          icon={<BellOutlined style={{ fontSize: 18, color: iconColor }} />}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 36,
            width: 36,
          }}
        />
      </Badge>

      <Modal
        title={t('notifications.title')}
        open={open}
        onCancel={() => setOpen(false)}
        footer={
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button
              type="link"
              icon={<CheckOutlined />}
              disabled={unreadCount === 0}
              onClick={markAllAsRead}
            >
              {t('notifications.markAllRead')}
            </Button>
            <Space>
              {hasMore ? (
                <Button type="primary" onClick={goToNotificationsPage}>
                  {t('notifications.showMore')}
                </Button>
              ) : null}
              <Button onClick={() => setOpen(false)}>{t('common.cancel') || 'Cancel'}</Button>
            </Space>
          </Space>
        }
        width={480}
        destroyOnClose
      >
        <List
          loading={loading}
          locale={{ emptyText: <Empty description={t('notifications.empty')} /> }}
          dataSource={items}
          renderItem={(item) => (
            <List.Item
              key={item.id}
              onClick={() => handleItemClick(item)}
              style={{
                cursor: 'pointer',
                background: item.is_read
                  ? 'transparent'
                  : theme === 'dark'
                    ? 'rgba(0, 164, 180, 0.18)'
                    : 'rgba(0, 164, 180, 0.08)',
                borderRadius: 8,
                paddingInline: 12,
                marginBottom: 4,
              }}
            >
              <List.Item.Meta
                avatar={<MailOutlined style={{ fontSize: 18, color: '#00a4b4', marginTop: 4 }} />}
                title={
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Typography.Text strong={!item.is_read}>{item.title}</Typography.Text>
                    {!item.is_read ? (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {t('notifications.unread')}
                      </Typography.Text>
                    ) : null}
                  </Space>
                }
                description={
                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                    <Typography.Text type="secondary">{item.body}</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {item.created_at
                        ? dayjs(item.created_at).fromNow()
                        : ''}
                    </Typography.Text>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Modal>
    </>
  );
};

export default NotificationsBell;
